import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTRPC } from '@/trpc/react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Clock, TrendingDown, TrendingUp } from 'lucide-react'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'

export const Route = createFileRoute('/dashboard/admin/super_admin/clock-stats')({
  component: RouteComponent,
})

const chartConfig = {
  clockIns: {
    label: 'Clock Ins',
    color: 'hsl(142, 76%, 36%)', // Green-600
  },
  clockOuts: {
    label: 'Clock Outs',
    color: 'hsl(217, 91%, 60%)', // Blue-500
  },
} as const

type TimeRange = '1d' | '3d' | '1w' | '1m' | '3m'
type EventType = 'both' | 'clockIn' | 'clockOut'

function RouteComponent() {
  const api = useTRPC()
  const [timeRange, setTimeRange] = useState<TimeRange>('1w')
  const [eventType, setEventType] = useState<EventType>('both')
  const [businessId, setBusinessId] = useState<string | null>(null)

  // Fetch organizations for the dropdown
  const { data: organizations = [] } = useQuery({
    ...api.superAdmin.getOrganizations.queryOptions(),
  })

  // Calculate date range based on timeRange
  const now = DateTime.now()
  let days = 7 // default 1 week
  if (timeRange === '1d') days = 1
  else if (timeRange === '3d') days = 3
  else if (timeRange === '1w') days = 7
  else if (timeRange === '1m') days = 30
  else if (timeRange === '3m') days = 90

  const from = now.minus({ days }).toFormat('yyyy-MM-dd')
  const to = now.toFormat('yyyy-MM-dd')

  const {
    data: clockStats,
    isPending,
    error,
  } = useQuery({
    ...api.superAdmin.getClockInOutStats.queryOptions({
      from,
      to,
      eventType,
      businessId,
    }),
  })

  // Generate 24-hour chart data
  const chartData = Array.from({ length: 24 }, (_, hour) => {
    // Format hour in AM/PM format using Luxon
    const hourLabel = DateTime.fromObject({ hour }).toFormat('h:mm a')

    const hourData: { hour: string; clockIns: number; clockOuts: number } = {
      hour: hourLabel,
      clockIns: 0,
      clockOuts: 0,
    }

    if (clockStats) {
      const hourClockIns = clockStats.hourlyData.find(
        (d: { hour: number; type: string; count: number }) =>
          d.hour === hour && d.type === 'clockIn',
      )
      const hourClockOuts = clockStats.hourlyData.find(
        (d: { hour: number; type: string; count: number }) =>
          d.hour === hour && d.type === 'clockOut',
      )

      hourData.clockIns = hourClockIns?.count ?? 0
      hourData.clockOuts = hourClockOuts?.count ?? 0
    }

    return hourData
  })

  return (
    <DashboardShell>
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Clock In/Out Statistics</h1>
          <p className="text-muted-foreground">
            Analytics on employee clock in and clock out times across all organizations
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Clock Ins</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{clockStats?.totalClockIns ?? 0}</div>
              <p className="text-xs text-muted-foreground">
                {timeRange === '1d'
                  ? 'Last 24 hours'
                  : timeRange === '3d'
                    ? 'Last 3 days'
                    : timeRange === '1w'
                      ? 'Last week'
                      : timeRange === '1m'
                        ? 'Last month'
                        : 'Last 3 months'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Clock Outs</CardTitle>
              <TrendingDown className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{clockStats?.totalClockOuts ?? 0}</div>
              <p className="text-xs text-muted-foreground">
                {timeRange === '1d'
                  ? 'Last 24 hours'
                  : timeRange === '3d'
                    ? 'Last 3 days'
                    : timeRange === '1w'
                      ? 'Last week'
                      : timeRange === '1m'
                        ? 'Last month'
                        : 'Last 3 months'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Peak Clock In Hour</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {clockStats?.peakClockInHour !== null && clockStats?.peakClockInHour !== undefined
                  ? DateTime.fromObject({ hour: clockStats.peakClockInHour }).toFormat('h:mm a')
                  : 'N/A'}
              </div>
              <p className="text-xs text-muted-foreground">
                {clockStats?.peakClockInCount ?? 0} clock ins
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Peak Clock Out Hour</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {clockStats?.peakClockOutHour !== null &&
                clockStats?.peakClockOutHour !== undefined
                  ? DateTime.fromObject({ hour: clockStats.peakClockOutHour }).toFormat('h:mm a')
                  : 'N/A'}
              </div>
              <p className="text-xs text-muted-foreground">
                {clockStats?.peakClockOutCount ?? 0} clock outs
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Chart */}
        <Card>
          <CardHeader className="flex items-center gap-2 space-y-0 border-b py-5 sm:flex-row">
            <div className="grid flex-1 gap-1 text-center sm:text-left">
              <CardTitle>Clock In/Out by Hour</CardTitle>
              <CardDescription>
                Distribution of clock ins and clock outs throughout the day (24-hour cycle)
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Select
                value={businessId ?? 'all'}
                onValueChange={(value) => setBusinessId(value === 'all' ? null : value)}
              >
                <SelectTrigger className="w-[200px] rounded-lg" aria-label="Select business">
                  <SelectValue placeholder="All Businesses" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="all" className="rounded-lg">
                    All Businesses
                  </SelectItem>
                  {organizations.map((org) => (
                    <SelectItem
                      key={org.businessId}
                      value={org.businessId ?? ''}
                      className="rounded-lg"
                    >
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={timeRange}
                onValueChange={(value) => setTimeRange(value as TimeRange)}
              >
                <SelectTrigger className="w-[140px] rounded-lg" aria-label="Select time range">
                  <SelectValue placeholder="1 week" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="1d" className="rounded-lg">
                    1 Day
                  </SelectItem>
                  <SelectItem value="3d" className="rounded-lg">
                    3 Days
                  </SelectItem>
                  <SelectItem value="1w" className="rounded-lg">
                    1 Week
                  </SelectItem>
                  <SelectItem value="1m" className="rounded-lg">
                    1 Month
                  </SelectItem>
                  <SelectItem value="3m" className="rounded-lg">
                    3 Months
                  </SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={eventType}
                onValueChange={(value) => setEventType(value as EventType)}
              >
                <SelectTrigger className="w-[160px] rounded-lg" aria-label="Select event type">
                  <SelectValue placeholder="Both" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="both" className="rounded-lg">
                    Both
                  </SelectItem>
                  <SelectItem value="clockIn" className="rounded-lg">
                    Clock Ins Only
                  </SelectItem>
                  <SelectItem value="clockOut" className="rounded-lg">
                    Clock Outs Only
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
            {isPending ? (
              <div className="flex h-[400px] items-center justify-center">
                <span className="text-muted-foreground">Loading clock stats...</span>
              </div>
            ) : error ? (
              <div className="flex h-[400px] items-center justify-center">
                <span className="text-destructive">Failed to load data</span>
              </div>
            ) : (
              <ChartContainer config={chartConfig} className="aspect-auto h-[400px] w-full">
                <BarChart data={chartData} margin={{ left: 0, right: 0, top: 10, bottom: 0 }}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="hour"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    minTickGap={32}
                  />
                  <YAxis tickLine={false} axisLine={false} tickMargin={8} />
                  <ChartTooltip
                    cursor={false}
                    content={<ChartTooltipContent indicator="line" />}
                  />
                  {eventType === 'both' || eventType === 'clockIn' ? (
                    <Bar
                      dataKey="clockIns"
                      fill="var(--color-clockIns)"
                      radius={4}
                      name="Clock Ins"
                    />
                  ) : null}
                  {eventType === 'both' || eventType === 'clockOut' ? (
                    <Bar
                      dataKey="clockOuts"
                      fill="var(--color-clockOuts)"
                      radius={4}
                      name="Clock Outs"
                    />
                  ) : null}
                </BarChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardShell>
  )
}
